import React, { useContext, useEffect, useMemo, useState } from 'react';

import { ThemeContext } from '../../store/ThemeProvider';
import request from '../../utils/request';
import ComponentLink from '../ComponentLink';

import './GuestWall.scss';

export interface GuestWallItem {
  id: string | number;
  personId?: string | number;
  name: string;
  avatar: string;
  organization?: string;
  position?: string;
  tags?: string[];
  photoCount?: number;
}

export interface GuestWallProps {
  title?: string;
  subtitle?: string;
  template?: 'card' | 'compact';
  'auto_show_all'?: boolean;
  'guest_ids'?: Array<string | number>;
}

const normalizeGuest = (item: any): GuestWallItem => {
  const guest = item.guest_service || item || {};
  const describe = guest.describe || {};
  const relations = item.guest_person_realtion || item.guest_person_relation || guest.guest_person_realtion || [];
  const relation = Array.isArray(relations) ? relations[0] : null;
  return {
    ...guest,
    id: guest.id,
    personId: guest['person_id'] || describe['person_id'] || (relation && (relation.person_id || relation.persion_id)),
    name: guest.name || '',
    avatar: guest.avatar || '',
    organization: guest.organization || guest.organization_name || '',
    position: guest.position || guest.title || '',
    tags: Array.isArray(guest.tags) ? guest.tags : (Array.isArray(guest.identity) ? guest.identity : []),
    photoCount: Number(item['photo_count'] || item.count || 0),
  };
};

const getPageLinkId = () => {
  return (
    (window.pageDetail && window.pageDetail.link_id) ||
    (window.gallery && (window.gallery.url_labeling || window.gallery.link)) ||
    ''
  );
};

const getGuestList = (guestIds: Array<string | number>) => {
  const orderId = window.gallery && window.gallery.orderid;
  const data: any = { 'order_id': orderId };
  if (guestIds.length) data.ids = guestIds.join(',');

  return request({
    url: '/api/v2/guest-wall/list',
    method: 'GET',
    data,
  });
};

const GuestWall: React.FC<GuestWallProps> = props => {
  const title = props.title === undefined ? '嘉宾墙' : props.title;
  const subtitle = (props.subtitle || '').trim() || (window.gallery && window.gallery.name) || '';
  const template = props.template || 'card';
  const autoShowAll = props['auto_show_all'] === undefined ? true : props['auto_show_all'];
  const guestIds = props['guest_ids'] || [];
  const { mainColor, h1Color, h2Color, componentColor } = useContext(ThemeContext);
  const [items, setItems] = useState<GuestWallItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getGuestList(autoShowAll ? [] : guestIds)
      .then((res: any) => {
        if (!active) return;
        const list = (res && (res.items || (res.data && res.data.items))) || [];
        setItems(list.map(normalizeGuest));
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [autoShowAll, JSON.stringify(guestIds)]);

  const orderedItems = useMemo(() => {
    if (autoShowAll || !guestIds.length) return items;
    return guestIds
      .map(id => items.find(item => String(item.id) === String(id)))
      .filter(Boolean) as GuestWallItem[];
  }, [items, autoShowAll, JSON.stringify(guestIds)]);

  const toDetail = (item: GuestWallItem) => {
    const linkId = getPageLinkId();
    if (!linkId) return;
    ComponentLink.to(`/page/guest/${linkId}/${item.id}`);
  };

  const toPhotos = (item: GuestWallItem) => {
    const linkId = getPageLinkId();
    if (!linkId) return;
    ComponentLink.to(`/page/guest/${linkId}/${item.id}/photos`);
  };

  if (!loading && orderedItems.length === 0) return null;

  return (
    <section className={`guest-wall-h5 ${template}`} style={{ backgroundColor: componentColor }}>
      {title ? <h2 className="guest-wall-title" style={{ color: h1Color }}><span>{title}</span></h2> : null}
      {subtitle ? <div className="guest-wall-subtitle" style={{ color: h2Color }}>{subtitle}</div> : null}
      {loading ? <div className="guest-wall-state" style={{ color: h2Color }}>嘉宾加载中...</div> : null}
      <div className="guest-wall-list">
        {orderedItems.map(item => (
          <article className="guest-wall-item" key={item.id}>
            <div className="guest-wall-card-content">
              <img src={item.avatar} alt={item.name} />
              <div className="guest-wall-info">
                <div className="guest-wall-name" style={{ color: '#000' }}>{item.name}</div>
                <div className="guest-wall-meta" style={{ color: '#000' }}>{item.organization}</div>
                <div className="guest-wall-meta" style={{ color: '#000' }}>{item.position}</div>
                {item.tags && item.tags[0] ? (
                  <span className="guest-wall-tag" style={{ color: mainColor, backgroundColor: `${mainColor}18` }}>
                    {item.tags[0]}
                  </span>
                ) : null}
              </div>
              <div className="guest-wall-buttons">
                <button style={{ color: '#fff', backgroundColor: mainColor }} onClick={() => toDetail(item)}>查看详情</button>
                <button disabled={!item.personId} title={item.personId ? '' : '关联的人脸不存在或已被删除'} style={{ color: mainColor, borderColor: mainColor }} onClick={() => toPhotos(item)}>查看照片</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default GuestWall;
